// Copyright © 2022 Vewd Software AS.
//
// This file is part of Vewd Cloud,
// and includes Vewd Confidential Information.
// Distribution is strictly prohibited without Vewd's written consent.
import { countries, regions } from "@internal-node-modules/tivo-locales";
import get from "lodash-es/get";

import { trans } from "src/translations";

export interface Continent {
  code: string;
  name: string;
}

export interface Country {
  code: string;
  continents: string[];
  name: string;
  type: string;
}
/**
 * Backend returns 2-letter codes for each continent. Since we have to display
 * actual names, we will use conversion table as follows.
 *
 * KEYS MUST MATCH ENTRIES IN GeoMap/utils/continents.js!
 */
export const CONTINENTS: Record<string, Continent> = {
  AFRICA: { code: "ZA", name: trans.CONTINENTS__AFRICA() },
  ANTARCTICA: { code: "AQ", name: trans.CONTINENTS__ANTARCTICA() },
  ASIA: { code: "AS", name: trans.CONTINENTS__ASIA() },
  EUROPE: { code: "EU", name: trans.CONTINENTS__EUROPE() },
  NORTH_AMERICA: { code: "NA", name: trans.CONTINENTS__NORTH_AMERICA() },
  OCEANIA: { code: "OC", name: trans.CONTINENTS__OCEANIA() },
  SOUTH_AMERICA: { code: "SA", name: trans.CONTINENTS__SOUTH_AMERICA() },
  WORLD: { code: "ZZ", name: trans.CONTINENTS__WORLD() },
};

export const getContinentName = (code: string): string | undefined => {
  const continent = Object.values(CONTINENTS).find((c) => c.code === code);
  return get(continent, "name");
};

export const CONTINENT_WORLD: string = getContinentName("ZZ") as string;
export const CONTINENT_EUROPE: string = getContinentName("EU") as string;

export const isRealContinent = (c: string): boolean => c !== CONTINENT_WORLD;

export const getContinentNames = (): string[] => {
  const realContinents = Object.values(CONTINENTS).reduce<string[]>(
    (acc, continent) => {
      if (isRealContinent(continent.name)) {
        acc.push(continent.name);
      }
      return acc;
    },
    []
  );

  return [CONTINENT_WORLD, ...realContinents.sort()];
};

/**
 * Creates a map of country codes to their continents using the regions data
 * from tivo-locales library.
 */
const getCountryToContinentsMap = (): Map<string, string[]> => {
  const countryToContinents = new Map<string, string[]>();
  regions.forEach((region) => {
    region.countries.forEach((countryCode) => {
      const existing = countryToContinents.get(countryCode) || [];
      countryToContinents.set(countryCode, [...existing, region.name]);
    });
  });
  return countryToContinents;
};

export const getCountriesForContinent = (
  countries: Country[],
  continent: string
): Country[] => {
  if (!isRealContinent(continent)) {
    return countries;
  }

  // Find the region that matches the continent name
  const region = regions.find((r) => r.name === continent);

  if (!region) {
    return [];
  }

  // Filter countries that are in this region
  return countries.filter((country) => region.countries.includes(country.code));
};

export function getCountries() {
  const adaptedCountries: Country[] = countries.map((country) => ({
    type: "country",
    name: country.name,
    code: country["alpha-2"],
    continents: getCountryToContinentsMap().get(country["alpha-2"]) || [],
  }));

  return adaptedCountries;
}
